import type { ScoreImportSummary } from '@/composables/useScoreEntry';

/**
 * `out_of_range` comes from sms-be#484 (merged) but is not in the generated
 * types yet, so it is read defensively until the next codegen run.
 */
export function outOfRangeCount(summary: ScoreImportSummary | null | undefined): number {
  const value = (summary as { out_of_range?: number | null } | null | undefined)?.out_of_range;
  return typeof value === 'number' ? value : 0;
}

/**
 * Compressed per-reason lines for the dry-run preview; the full per-row detail
 * lives in the result dialog's table.
 */
export function previewIssueLines(summary: ScoreImportSummary | null | undefined, isCourseContext: boolean): string[] {
  if (!summary) return [];
  const lines: string[] = [];
  if (summary.unmatched > 0) {
    lines.push(
      isCourseContext
        ? `${summary.unmatched} row(s) will be skipped — no HOD-endorsed registration for this course in the selected calendar.`
        : `${summary.unmatched} row(s) will be skipped — no enrollment for this subject and classroom in the selected calendar.`,
    );
  }
  if (summary.missing_student_id > 0) lines.push(`${summary.missing_student_id} row(s) will be skipped — missing the hidden Student ID.`);
  if (summary.non_numeric > 0) lines.push(`${summary.non_numeric} value(s) will be ignored — not a number.`);
  const outOfRange = outOfRangeCount(summary);
  if (outOfRange > 0) lines.push(`${outOfRange} score(s) will be skipped — below zero or above the assessment's maximum.`);
  return lines;
}

/** Label for the upload button: "Confirm Import (N scores)" once a preview says something will import. */
export function uploadButtonLabel(summary: ScoreImportSummary | null | undefined): string {
  return summary && summary.saved > 0 ? `Confirm Import (${summary.saved} scores)` : 'Upload Scores';
}

/** Nothing to confirm while the preview runs, or when it says nothing can be imported. */
export function isUploadDisabled(hasFile: boolean, previewing: boolean, summary: ScoreImportSummary | null | undefined): boolean {
  return !hasFile || previewing || (!!summary && summary.saved === 0);
}
